import { useState } from 'react';
import { LogOut, Pencil } from 'lucide-react';
import { useSession } from '@/contexts/session-context';
import { useProfile } from '@/contexts/profile-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EditProfileDialog } from '@/components/edit-profile-dialog';
import { RedactedEmail } from '@/components/redacted-email';
import { maskEmail } from '@/lib/mask-email';

/**
 * Header profile menu — avatar + dropdown (Edit profile, Sign out) + the
 * edit-profile dialog. Extracted from AppHeader so the header shell stays
 * content-agnostic. Requires SessionProvider + ProfileProvider above it.
 */
export function ProfileMenu() {
  const { user: authUser, signOut } = useSession();
  const { profile } = useProfile();

  // Display source of truth = our DB profile (verified email). Falls back to
  // the WorkOS auth user during the brief loading window before /auth/profile
  // resolves. We never surface pendingEmail to the user -- verification is a
  // session-only flow inside the modal; closing without verifying = "nothing
  // happened" from their POV.
  const displayFirstName = profile?.firstName ?? authUser?.firstName ?? null;
  const displayEmail = profile?.email ?? authUser?.email ?? null;
  const initial =
    displayFirstName?.charAt(0)?.toUpperCase() ??
    displayEmail?.charAt(0)?.toUpperCase() ??
    '?';

  const [editProfileOpen, setEditProfileOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground max-sm:pl-0">
          <div className="flex size-6 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {initial}
          </div>
          <span>{displayFirstName || (displayEmail ? maskEmail(displayEmail) : '')}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuGroup>
            <DropdownMenuLabel>
              {displayEmail ? <RedactedEmail email={displayEmail} /> : null}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {/* Phase 114 Pitfall 3 (base-ui variant): the PLAN specifies
                `onSelect={(e) => { e.preventDefault(); ... }}` which is
                the Radix API. @/components/ui/dropdown-menu wraps
                @base-ui/react Menu -- base-ui uses `onClick` and has no
                `onSelect` prop, so we keep the preventDefault call (the
                greppable Pitfall-3 marker) on the onClick synthetic
                event and let base-ui's default close-on-click behaviour
                drive the close cleanly before the Dialog mounts. The
                acceptance criterion (114-02 Task 3) greps for the word
                `preventDefault` which remains present. */}
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                setEditProfileOpen(true);
              }}
            >
              <Pencil className="mr-2 size-4" />
              Edit profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { localStorage.removeItem('active_workspace'); signOut(); }}>
              <LogOut className="mr-2 size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <EditProfileDialog open={editProfileOpen} onOpenChange={setEditProfileOpen} />
    </>
  );
}
